import React from 'react';
import type { CanvasObject } from '../../../types';

type Props = {
	getSelectedObjects: () => CanvasObject[];
	updateObject: (id: string, updater: (o: CanvasObject) => CanvasObject) => void;
	requestRender: () => void;
	saveToHistory: (state: { objects: Map<string, CanvasObject>; zOrder: string[]; selectedId: string | null }) => void;
	objects: Map<string, CanvasObject>;
	zOrder: string[];
	selectedId: string | null;
};

export const OpacityControl: React.FC<Props> = ({
	getSelectedObjects,
	updateObject,
	requestRender,
	saveToHistory,
	objects,
	zOrder,
	selectedId
}) => {
	const [tempOpacity, setTempOpacity] = React.useState<string>('');
	const focusedRef = React.useRef<boolean>(false);
	const prevDisplayedRef = React.useRef<number | null>(null);

	// 표시용 값 계산 (다중 선택 시 첫 번째 객체 기준)
	const displayedOpacity = React.useMemo(() => {
		const selected = getSelectedObjects();
		if (selected.length === 0) return 100;
		if (tempOpacity !== '') {
			const n = Number(tempOpacity);
			return isNaN(n) ? 100 : n;
		}
		return selected[0].opacity ?? 100;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getSelectedObjects, tempOpacity, selectedId, objects]);

	// 외부 변경과 입력중 덮어쓰기 방지 동기화
	React.useEffect(() => {
		const prev = prevDisplayedRef.current;
		const hasChanged = prev === null || prev !== displayedOpacity;
		if (hasChanged) {
			if (!focusedRef.current) {
				// 입력 포커스가 아닐 때만 임시값 초기화
				if (tempOpacity !== '' && tempOpacity !== String(displayedOpacity)) {
					setTempOpacity('');
				}
			}
			prevDisplayedRef.current = displayedOpacity;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [displayedOpacity, tempOpacity]);

	return (
		<div className="info-field" style={{ padding: '0 0 10px' }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				<label className="info-panel__label" style={{ margin: 0, whiteSpace: 'nowrap' }}>Opacity</label>
				<input
					type="number"
					className="info-panel__input"
					style={{ width: 100 }}
					min={0}
					max={100}
					step={1}
					value={tempOpacity !== '' ? tempOpacity : String(displayedOpacity)}
					onFocus={(e) => { 
						focusedRef.current = true;
						e.target.select();
					}}
					onChange={(e) => setTempOpacity(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							(e.currentTarget as HTMLInputElement).blur();
						}
					}}
					onBlur={(e) => {
						focusedRef.current = false;
						const val = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
						setTempOpacity('');
						const selected = getSelectedObjects();
						if (selected.length > 0) {
							selected.forEach(obj => {
								updateObject(obj.id, (o) => ({ ...o, opacity: val }));
							});
							requestRender();
							saveToHistory({ objects: new Map(objects), zOrder: [...zOrder], selectedId });
						}
					}}
				/>
			</div>
		</div>
	);
};


